/**
 * React Query hook for fetching new matches (derived from all matches)
 */

import { matchesService } from "@features/messaging/services/matchesService";
import { deriveMatchStatus } from "@features/messaging/utils/matchUtils";
import { matchesQueryKeys } from "@shared/queryKeys";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch new matches (users who matched, no conversation yet).
 * Uses the same cache as useAllMatches; filters client-side via select.
 */
export function useNewMatches(userId: string) {
  return useQuery({
    queryKey: matchesQueryKeys.allMatches(userId),
    queryFn: async () => matchesService.getAllMatches(userId),
    select: (data) =>
      data
        .filter((m) => deriveMatchStatus(m) === "new")
        .sort((a, b) => (a.user.display_name ?? "").localeCompare(b.user.display_name ?? "")),
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute (shared with all matches)
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
